import { NavLink, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getAccessibleDashboards } from '../../utils/dashboardRecommendations';
import { useEscalationCounts } from '../../hooks/useEscalationCounts';
import { useOIAlertDispatch } from '../../hooks/useOIAlertDispatch';
import Logo from '../Logo';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Warehouse,
  Users,
  FileText,
  Building2,
  TrendingUp,
  ArrowLeftRight,
  Truck,
  ClipboardCheck,
  DollarSign,
  Camera,
  Settings,
  ShoppingBag,
  Activity,
  Zap,
  BarChart3,
  Shield,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Package2,
  Target,
  Database,
  Map,
  CreditCard,
  X
} from 'lucide-react';

// Dashboard items array
const dashboardItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Overview', description: 'Main dashboard' },
  { to: '/dashboards/executive', icon: Briefcase, label: 'Executive', description: 'Revenue & profit' },
  { to: '/dashboards/operations', icon: Package2, label: 'Operations', description: 'Inventory & fulfillment' },
  { to: '/dashboards/purchasing', icon: ShoppingCart, label: 'Purchasing', description: 'POs & suppliers' },
  { to: '/dashboards/amazon', icon: ShoppingBag, label: 'Amazon Manager', description: 'FBA metrics' },
];

// Core modules navigation groups
const navGroups = [
  {
    title: 'CORE MODULES',
    items: [
      { to: '/operations/command', icon: Zap, label: 'Operations', description: 'Active issues & execution queue' },
      { to: '/inventory/products', icon: Package, label: 'Products', escalationKey: 'products' },
      { to: '/orders', icon: ShoppingCart, label: 'Orders', escalationKey: 'orders' },
      { to: '/purchase-orders', icon: FileText, label: 'Purchase Orders', escalationKey: 'purchaseOrders' },
      { to: '/suppliers', icon: Users, label: 'Suppliers', escalationKey: 'suppliers' },
      { to: '/warehouses', icon: Building2, label: 'Warehouses' },
      { to: '/transfers', icon: ArrowLeftRight, label: 'Transfers' },
      { to: '/fba-shipments', icon: Truck, label: 'FBA Shipments' },
      { to: '/quality/inspections', icon: ClipboardCheck, label: 'Quality Control' },
      { to: '/scanner/receive', icon: Camera, label: 'Scanner' },
      { to: '/inventory/movements', icon: Warehouse, label: 'Movements' },
    ]
  },
  {
    title: 'ANALYTICS',
    items: [
      { to: '/analytics', icon: TrendingUp, label: 'Analytics' },
      { to: '/analytics/search', icon: BarChart3, label: 'Search Analytics' },
      { to: '/finance', icon: DollarSign, label: 'Cash Flow' },
      { to: '/warehouses/map', icon: Map, label: 'Warehouse Map' },
      { to: '/forecasting', icon: Activity, label: 'Forecasting' },
      { to: '/automation/reorder', icon: Zap, label: 'Auto-Reorder' },
      { to: '/reports', icon: BarChart3, label: 'Reports' },
    ]
  },
  {
    title: 'ADMIN',
    items: [
      { to: '/subscription', icon: CreditCard, label: 'Subscription' },
      { to: '/audit-logs', icon: Shield, label: 'Audit Trail' },
      { to: '/integrations/amazon', icon: ShoppingBag, label: 'Amazon' },
      { to: '/admin/cache-monitor', icon: Database, label: 'Cache Monitor' },
      { to: '/settings', icon: Settings, label: 'Settings' },
    ]
  }
];

const Sidebar = ({ collapsed = false, onClose }) => {
  const { user, _logout, hasPermission } = useAuth();
  const escalationCounts = useEscalationCounts();
  useOIAlertDispatch(escalationCounts.escalatedItems);
  const [dashboardsOpen, setDashboardsOpen] = useState(true);
  const [coreModulesOpen, setCoreModulesOpen] = useState(true);
  const [analyticsOpen, setAnalyticsOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(false);

  // Filter admin items based on permissions
  const adminItems = useMemo(() => {
    const items = navGroups[2]?.items || [];
    return items.filter(item => {
      // Cache Monitor requires system:admin
      if (item.to === '/admin/cache-monitor') {
        return hasPermission('system:admin');
      }
      return true;
    });
  }, [hasPermission]);

  // Filter dashboards based on user permissions
  const accessibleDashboards = useMemo(() => {
    if (!user) return dashboardItems;
    const accessible = getAccessibleDashboards(user);
    return dashboardItems.filter(item =>
      accessible.some(d => d.path === item.to)
    );
  }, [user]);

  return (
    <aside className={`${collapsed ? 'w-20' : 'w-64'} h-full bg-white dark:bg-dark-900 border-r border-gray-200 dark:border-dark-700 flex flex-col transition-all duration-300`}>
      {/* Header with Brand Icon + System Status */}
      <div className="h-16 px-4 flex items-center justify-between border-b border-gray-200 dark:border-dark-700">
        {/* GH Icon as Home Button (collapsed) or Status (expanded) */}
        {collapsed ? (
          <Link
            to="/dashboard"
            className="flex items-center justify-center w-full hover:opacity-80 transition-opacity"
            title="Go to Dashboard"
          >
            <Logo size="sm" variant="mini" />
          </Link>
        ) : (
          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 dark:bg-dark-800 rounded-md border border-gray-200 dark:border-dark-700">
            <div className="relative flex-shrink-0">
              <div className="w-1.5 h-1.5 bg-brand-500 rounded-full"></div>
              <div className="absolute inset-0 w-1.5 h-1.5 bg-brand-500 rounded-full animate-ping opacity-50"></div>
            </div>
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 tracking-wide uppercase">Live</span>
          </div>
        )}
        {/* Mobile close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors"
            aria-label="Close navigation menu"
          >
            <X size={20} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto" aria-label="Main navigation">
        {/* DASHBOARDS Section */}
        {!collapsed && (
        <div className="mb-3">
          <button
            onClick={() => setDashboardsOpen(!dashboardsOpen)}
            aria-expanded={dashboardsOpen}
            aria-controls="dashboards-menu"
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-800 transition-all duration-200"
          >
            <div className="flex items-center gap-2">
              <Target size={20} className="text-brand-500" aria-hidden="true" />
              <span className="nav-group-title text-gray-500 dark:text-gray-400">Dashboards</span>
            </div>
            <span className={`transition-transform duration-200 ${dashboardsOpen ? 'rotate-180' : ''}`} aria-hidden="true">
              <ChevronDown size={16} />
            </span>
          </button>

          {dashboardsOpen && (
            <div id="dashboards-menu" className="mt-1 space-y-0.5" role="menu">
              {accessibleDashboards?.map((item, index) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  role="menuitem"
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 px-3 py-2 ml-2 rounded-lg transition-all duration-200 animate-fade-in-up ${
                      isActive
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800 hover:translate-x-1'
                    }`
                  }
                  style={{ animationDelay: `${index * 30}ms` }}
                  title={item.label}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-brand-500 rounded-r-full"></div>
                      )}
                      <item.icon size={18} className={isActive ? 'text-brand-500' : ''} />
                      <span className="nav-label text-sm">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          )}
        </div>
        )}

        {/* Collapsed state - icon-only navigation */}
        {collapsed && accessibleDashboards?.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `group relative flex items-center justify-center p-3 rounded-lg transition-colors ${
                isActive
                  ? 'bg-brand-50 dark:bg-brand-900/20 text-gray-900 dark:text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800'
              }`
            }
            title={item.label}
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-brand-500 rounded-r-full"></div>
                )}
                <item.icon size={20} className={isActive ? 'text-brand-500' : ''} />
              </>
            )}
          </NavLink>
        ))}

        {/* Divider */}
        <div className="h-px bg-gray-200 dark:bg-dark-700 my-3"></div>

        {/* CORE MODULES Section */}
        <div className="mb-3">
          <button
            onClick={() => setCoreModulesOpen(!coreModulesOpen)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Package size={20} className="text-brand-500" />
              <span className="nav-group-title text-gray-500 dark:text-gray-400">Core Modules</span>
            </div>
            {coreModulesOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          {coreModulesOpen && (
            <div className="mt-1 space-y-0.5">
              {navGroups[0]?.items?.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 px-3 py-2 ml-2 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-brand-500 rounded-r-full"></div>
                      )}
                      <item.icon size={18} className={isActive ? 'text-brand-500' : ''} />
                      <span className="nav-label text-sm">{item.label}</span>
                      {!collapsed && item.escalationKey && (escalationCounts[item.escalationKey] ?? 0) > 0 && (
                        <span className="ml-auto text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 leading-none shrink-0">
                          {escalationCounts[item.escalationKey] > 99 ? '99+' : escalationCounts[item.escalationKey]}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-200 dark:bg-dark-700 my-3"></div>

        {/* ANALYTICS Section */}
        <div className="mb-3">
          <button
            onClick={() => setAnalyticsOpen(!analyticsOpen)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
          >
            <div className="flex items-center gap-2">
              <TrendingUp size={20} className="text-brand-500" />
              <span className="nav-group-title text-gray-500 dark:text-gray-400">Analytics</span>
            </div>
            {analyticsOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          {analyticsOpen && (
            <div className="mt-1 space-y-0.5">
              {navGroups[1]?.items?.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 px-3 py-2 ml-2 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-brand-500 rounded-r-full"></div>
                      )}
                      <item.icon size={18} className={isActive ? 'text-brand-500' : ''} />
                      <span className="nav-label text-sm">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-px bg-gray-200 dark:bg-dark-700 my-3"></div>

        {/* ADMIN Section */}
        <div className="mb-3">
          <button
            onClick={() => setAdminOpen(!adminOpen)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-800 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Shield size={20} className="text-brand-500" />
              <span className="nav-group-title text-gray-500 dark:text-gray-400">Admin</span>
            </div>
            {adminOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>

          {adminOpen && (
            <div className="mt-1 space-y-0.5">
              {adminItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 px-3 py-2 ml-2 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-800'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-brand-500 rounded-r-full"></div>
                      )}
                      <item.icon size={18} className={isActive ? 'text-brand-500' : ''} />
                      <span className="nav-label text-sm">{item.label}</span>
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Removed - Profile & Logout now in top bar dropdown */}
    </aside>
  );
};

export default Sidebar;
